import { Fragment } from 'react';
import walletIcon from '@/shared/assets/icons/wallet.svg';
import ribbonIcon from '@/shared/assets/icons/ribbon.svg';
import logoutIcon from '@/shared/assets/icons/logout.svg';
import type { ListingFaqItem } from '../../domain/models/Seo';

function replaceBaseUrlPlaceholder(text: string): string {
  return text.replace(
    /__BASE_URL__/g,
    typeof window !== 'undefined' ? window.location.origin : '__BASE_URL__'
  );
}

/**
 * Certification / trust badges section (30 Day Money Back, Certified Jewellery, Lifetime Exchange).
 * Rendered as a separate section from SEO content.
 */
export function CertificationSection() {
  return (
    <section className="border-t border-gray-200 bg-white" aria-label="Certification and assurance">
      <div className="mx-auto grid max-w-screen-md grid-cols-3 gap-3 items-stretch px-4 py-4">
        <a href="/shipping-return.html#30-day-return" rel="nofollow" className="flex flex-col items-center justify-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-4 text-center hover:border-gray-300">
          <img src={logoutIcon} alt="" className="h-8 w-8 shrink-0 text-gray-600" aria-hidden />
          <span className="text-xs font-semibold text-gray-800">30 DAY MONEY BACK</span>
        </a>
        <a href="/certifications.html" rel="nofollow" className="flex flex-col items-center justify-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-4 text-center hover:border-gray-300">
          <img src={ribbonIcon} alt="" className="h-8 w-8 shrink-0 text-gray-600" aria-hidden />
          <span className="text-xs font-semibold text-gray-800">CERTIFIED JEWELLERY</span>
        </a>
        <a href="/shipping-return.html#lifetime-exchange" rel="nofollow" className="flex flex-col items-center justify-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-4 text-center hover:border-gray-300">
          <img src={walletIcon} alt="" className="h-8 w-8 shrink-0 text-gray-600" aria-hidden />
          <span className="text-xs font-semibold text-gray-800">LIFETIME EXCHANGE</span>
        </a>
      </div>
    </section>
  );
}

/**
 * Renders browse PLP footer SEO: category title, `seoText` HTML, and API FAQs.
 * Mirrors legacy `productListingV1/index.js` (seo content wrap + FAQ block).
 */
export function PlpSeoTextBlock({
  seoText,
  title,
  faqs,
}: {
  seoText: string | null | undefined;
  /** Category title shown above SEO content (e.g. "Necklaces") */
  title?: string | null;
  faqs?: ListingFaqItem[] | null;
}) {
  const hasSeoText = Boolean(seoText?.trim());
  const faqItems = faqs?.filter((f) => f.question.trim() && f.answer.trim()) ?? [];
  const hasFaqs = faqItems.length > 0;
  if (!hasSeoText && !hasFaqs) return null;

  const displayTitle = title?.trim() ?? '';

  return (
    <section className="border border-gray-200 bg-white" aria-label="SEO content">
      <div className="mx-auto px-8 py-8">
        {displayTitle && (
          <h2 className="mb-4 border-b border-[#d6d6d6] pb-2.5 text-2xl font-normal text-black">
            {displayTitle}
          </h2>
        )}
        {hasSeoText && (
          <div
            className="plp-seo-content"
            dangerouslySetInnerHTML={{ __html: replaceBaseUrlPlaceholder(seoText!) }}
          />
        )}
        {hasFaqs && (
          <div className="plp-seo-content">
            {displayTitle && (
              <h2>Frequently Asked Questions About {displayTitle}</h2>
            )}
            {faqItems.map((faq, index) => (
              <Fragment key={index}>
                <h3>{replaceBaseUrlPlaceholder(faq.question)}</h3>
                <p>{replaceBaseUrlPlaceholder(faq.answer)}</p>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
